import { Outlet, useNavigate, useParams, useSearchParams } from 'react-router';
import { AccessControlAction, AppRoutes, AppRoutesSearchParams, CLOSE_CASE_CONFIRM } from '@/constants';
import { WarningConfirmationDialog } from '@/ui/components/WarningConfirmationDialog';
import { CaseTemplate } from '@/ui/templates';
import { ActionsPanel } from './components';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  selectCaseDocumentsPagination,
  selectCaseStatus,
  selectIsEndpointLockActive,
  setCaseIdData,
  setCaseMetadata,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { useLazyGetCaseOverviewQuery } from '@/api/case/caseOverview';
import {
  announceEndpointLockRefusal,
  api,
  EndpointLock,
  useLazyGetCaseDocumentsQuery,
  useLazyGetCaseQuery,
  useLazyGetCaseStatusQuery,
  useLazyGetUsersListQuery,
} from '@/api';
import { CreateEditCaseSidebar, CloneCaseSidebar } from '../components';
import { CaseInfoSidebar } from '@/ui/components/CaseInfoSidebar';
import { FileDropOverlay } from '@/ui/components/FileDropOverlay';
import { useFileDropZone } from '@/ui/components/useFileDropZone';
import { useAccessControl, useCaseCloningLock, useForceCaseStatusRefresh } from '@/hooks';
import { BROADCAST_FORCE_CASE_OVERVIEW_CARDS_REFRESH } from '@/broadcast';
import { removeSearchParams } from '@/utils';
import { CaseStatus } from './utils';
import { useDragAndDropFilesUpload } from './UploadedMedicalRecords/hooks';
export const CaseItemLayout = () => {
  const navigate = useNavigate();
  const { caseId } = useParams();
  const dispatch = useAppDispatch();
  const [isCreateEditCaseSidebarVisible, setIsCreateEditCaseSidebarVisible] = useState<boolean>(false);
  const [isCloneCaseSidebarVisible, setIsCloneCaseSidebarVisible] = useState<boolean>(false);
  const caseDocumentsPagination = useAppSelector(selectCaseDocumentsPagination);
  const [triggerGetCase, { isLoading: isGetCaseLoading }] = useLazyGetCaseQuery();
  const [triggerGetCaseStatus, { data: caseStatusData }] = useLazyGetCaseStatusQuery();
  const [triggerGetCaseOverview] = useLazyGetCaseOverviewQuery();
  const [triggerGetCaseDocuments] = useLazyGetCaseDocumentsQuery();
  const [triggerGetUsersList, { isLoading: isGetUsersListLoading }] = useLazyGetUsersListQuery();
  const [searchParams, setSearchParams] = useSearchParams();
  const forceCaseStatusRefresh = useForceCaseStatusRefresh();

  useCaseCloningLock(caseId);

  // A tab opened for a new clone can read its status before the backend has flagged it, so it keeps checking for a
  // few seconds, like the tab that started the clone. Declared after useCaseCloningLock, whose listener must be in place.
  useEffect(() => {
    if (!caseId || searchParams.get(AppRoutesSearchParams.cloneStartedOnInit) !== 'true') return;

    forceCaseStatusRefresh(caseId);

    const updatedParams = removeSearchParams([AppRoutesSearchParams.cloneStartedOnInit], searchParams);
    if (updatedParams) {
      setSearchParams(updatedParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      dispatch(setCaseIdData(null));
      dispatch(setCaseMetadata(null));
      dispatch(api.util.resetApiState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    if (caseId) {
      dispatch(setCaseIdData(caseId));

      triggerGetCase({ caseId: caseId });

      triggerGetCaseStatus({ caseId: caseId });

      triggerGetCaseDocuments({
        caseId: caseId,
        ...caseDocumentsPagination,
      });

      triggerGetCaseOverview({ caseId: caseId });

      triggerGetUsersList({}, true);

      /**
       * Broadcast listener
       */
      const pagesUpdateInLightBundle = () => {
        if (signal.aborted) {
          console.info(`Listener Aborted: ${BROADCAST_FORCE_CASE_OVERVIEW_CARDS_REFRESH}`);
          return;
        }

        triggerGetCaseOverview({ caseId: caseId });
      };

      window.addEventListener(BROADCAST_FORCE_CASE_OVERVIEW_CARDS_REFRESH, pagesUpdateInLightBundle as EventListener, {
        signal,
      });
    }

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const handleOpenCaseSidebarClick = useCallback(() => {
    setIsCreateEditCaseSidebarVisible(true);
  }, []);

  const handleCloseCaseSidebarClick = useCallback(() => {
    setIsCreateEditCaseSidebarVisible(false);
  }, []);

  const handleOpenCloneCaseSidebarClick = useCallback(() => {
    setIsCloneCaseSidebarVisible(true);
  }, []);

  const handleCloseCloneCaseSidebarClick = useCallback(() => {
    setIsCloneCaseSidebarVisible(false);
  }, []);

  const caseStatus = useAppSelector(selectCaseStatus);
  // The lock rather than caseStatus.isCloning: it only reflects this case, while caseStatus can still hold the last
  // case's flags until this one's first read lands
  const isCaseCloning = useAppSelector((state) => selectIsEndpointLockActive(state, EndpointLock.CASE_CLONING));
  const isUploadDeleteRawMedicalRecords = useAccessControl(AccessControlAction.UPLOAD_DELETE_RAW_MEDICAL_RECORDS);
  // Mirrors the gate in ActionsPanel's UploadFilesButton — both
  // controls share one eligibility flag so a drag-drop ingestion
  // never succeeds while the manual button is hidden.
  const canUpload = isUploadDeleteRawMedicalRecords && !!caseStatus?.canUploadDocuments && !isCaseCloning;
  // While the case is being cloned a drop is still caught, but refused with the lock's toast instead of uploading.
  // The refusal re-reads the status too, as refused requests do, so a clone that has just finished stops blocking.
  const isUploadRefusedForClone = isUploadDeleteRawMedicalRecords && isCaseCloning;
  const { onFilesStartUpload } = useDragAndDropFilesUpload();
  const refuseDropForClone = () => {
    announceEndpointLockRefusal(EndpointLock.CASE_CLONING);
    if (caseId) triggerGetCaseStatus({ caseId });
  };
  const { overlayContainerRef, isDragging, error } = useFileDropZone({
    onFilesDropped: isUploadRefusedForClone ? refuseDropForClone : onFilesStartUpload,
    disabled: !canUpload && !isUploadRefusedForClone,
  });

  const hasInitialRedirectOccurred = useRef(false);

  useEffect(() => {
    // Only redirect to AI Processed Documents on initial load, not on subsequent navigation
    if (
      caseStatusData &&
      caseStatusData.status === CaseStatus.READY_FOR_REVIEW &&
      !hasInitialRedirectOccurred.current
    ) {
      hasInitialRedirectOccurred.current = true;

      // Re-fetch case overview to get updated bundle information
      // (e.g. when processing just finished and bundle data has changed)
      triggerGetCaseOverview({ caseId: caseId! });

      navigate(`/${AppRoutes.CaseList}/${caseId}/${AppRoutes.AiProcessedDocuments}`);
    }
  }, [caseStatusData, navigate, caseId, triggerGetCaseOverview]);
  return (
    <CaseTemplate
      isDataLoading={false}
      actionsPanel={
        <ActionsPanel
          handleOpenCaseSidebarClick={handleOpenCaseSidebarClick}
          handleOpenCloneCaseSidebarClick={handleOpenCloneCaseSidebarClick}
          isDataLoading={isGetCaseLoading || isGetUsersListLoading}
        />
      }
      leftSidebar={<CaseInfoSidebar />}
    >
      {/* Drop-anywhere overlay — works on both Uploaded Medical
          Records and AI Processed Documents tabs since it lives at
          the layout level. Dropped files always route to the
          uploaded-records pipeline, unless the case is being cloned. */}
      <FileDropOverlay
        overlayContainerRef={overlayContainerRef}
        isDragging={isDragging}
        error={error}
        title={isUploadRefusedForClone ? "Documents can't be added right now" : undefined}
        subtitle={isUploadRefusedForClone ? 'This case is being cloned' : 'Release to add documents to this case'}
      />
      {/* Close-case confirmation — shared warning dialog, reused across
          destructive actions (delete document, abandon AI, close case).
          Mounted here so the header ActionsPanel can trigger it via
          `confirmDialog({ id: CLOSE_CASE_CONFIRM })` regardless of which
          case sub-tab is active. */}
      <WarningConfirmationDialog id={CLOSE_CASE_CONFIRM} bodyText="This case will be closed." confirmLabel="Close" />
      <div className="flex h-full w-full flex-col overflow-auto px-6 pt-4 pb-4">
        {isCreateEditCaseSidebarVisible && <CreateEditCaseSidebar closeSidebar={handleCloseCaseSidebarClick} />}
        {isCloneCaseSidebarVisible && <CloneCaseSidebar closeSidebar={handleCloseCloneCaseSidebarClick} />}

        <Outlet />
      </div>
    </CaseTemplate>
  );
};
